import { api } from "./lib/http";
import type {
  AdminTask,
  AdminClub,
  AdminStats,
  AdminUser,
  ApiResponseList,
  Product,
  CreateProduct,
  UpdateProduct,
  Tournament,
  Club,
  CreatePublicRegistration,
  RegistrationConfirmResponse,
  CreateRegistration,
  Reservation,
  Court,
  CreateCourt,
  ProductCategory,
  CreateProductCategory,
  UpdateProductCategory,
  PointOfSale,
  CreateSaleItem,
  SaleItem,
  Sale,
  Player,
  CreatePlayer,
  Category,
  CreateOpenMatch,
  UpdateOpenMatch,
  WebOpenMatch,
} from "@repo/shared-types";

export { setToken, getToken, clearToken } from "./lib/auth";

export const authApi = {
  loginWithGoogle: (id_token: string) => api.post("/auth/google", { id_token }),
  loginWithEmail: ({ email, password }: { email: string; password: string }) =>
    api.post("/auth/email", { email, password }),
  getCurrentUser: () => api.get("/auth/me"),
};

export interface ClubRelations {
  members: number;
  tournaments: number;
  courts: number;
  products: number;
  productCategories: number;
  pointsOfSale: number;
  sales: number;
  players: number;
}

export const adminApi = {
  getTasks: () => api.get<AdminTask[]>("/admin/tasks"),
  execTask: (taskId: string) => api.post(`/admin/tasks/exec/${taskId}`),
  getClubs: () => api.get<AdminClub[]>("/admin/clubs"),
  createClub: (data: { name: string }) => api.post<AdminClub>("/admin/clubs", data),
  getClubById: (id: string) => api.get<AdminClub>(`/admin/clubs/${id}`),
  getClubRelations: (id: string) =>
    api.get<ClubRelations>(`/admin/clubs/${id}/relations`),
  getStats: () => api.get<AdminStats[]>("/admin/stats"),
  updateClubFeatures: (id: string, features: Record<string, boolean>) =>
    api.patch(`/admin/clubs/${id}/features`, features),
  deleteClub: (id: string) => api.delete(`/admin/clubs/${id}`),
  getCategories: () => api.get<Category[]>("/admin/categories"),
  updateCategory: (id: string, data: { isUserEligible: boolean }) =>
    api.patch<Category>(`/admin/categories/${id}`, data),
  getUsers: () => api.get<AdminUser[]>("/admin/users"),
  getUserById: (id: string) => api.get<AdminUser>(`/admin/users/${id}`),
  addUserToClub: (userId: string, data: { clubId: string; role: string }) =>
    api.post(`/admin/users/${userId}/members`, data),
  removeUserFromClub: (userId: string, memberId: string) =>
    api.delete(`/admin/users/${userId}/members/${memberId}`),
};

export const productApi = {
  createProductPos: (product: CreateProduct) =>
    api.post(`/pos/products`, product),
  updateImage: (clubId: string, productId: string, image: FormData) =>
    api.post(`/web-app/club/${clubId}/product/${productId}/image`, image),
  getSales: (clubId: string) =>
    api.get<ApiResponseList<Sale>>(`/web-app/club/${clubId}/sale`),
  createSale: (clubId: string, saleItems: string[]) =>
    api.post(`/web-app/club/${clubId}/sale`, saleItems),
  deleteSaleItems: (clubId: string, saleItems: string[]) =>
    api.delete(
      `/web-app/club/${clubId}/sale/item?saleItems=${saleItems.join(",")}`
    ),
  getSaleItems: (clubId: string) =>
    api.get<ApiResponseList<SaleItem>>(`/web-app/club/${clubId}/sale/item`),
  createSaleItem: (saleItem: CreateSaleItem) =>
    api.post(`/pos/sales`, saleItem),
  createSaleItemWeb: (clubId: string, saleItem: CreateSaleItem) =>
    api.post<ApiResponseList<SaleItem>>(
      `/web-app/club/${clubId}/sale/item`,
      saleItem
    ),
  getCategories: (clubId: string) =>
    api.get<ApiResponseList<ProductCategory>>(
      `/web-app/club/${clubId}/product-category`
    ),
  getCategoryById: (clubId: string, categoryId: string) =>
    api.get<ProductCategory>(
      `/web-app/club/${clubId}/product-category/${categoryId}`
    ),
  createCategory: (clubId: string, category: CreateProductCategory) =>
    api.post(`/web-app/club/${clubId}/product-category`, category),
  updateCategory: (
    clubId: string,
    categoryId: string,
    category: UpdateProductCategory
  ) =>
    api.patch(
      `/web-app/club/${clubId}/product-category/${categoryId}`,
      category
    ),
  deleteCategory: (clubId: string, categoryId: string) =>
    api.delete(`/web-app/club/${clubId}/product-category/${categoryId}`),
  updateProduct: (clubId: string, productId: string, product: UpdateProduct) =>
    api.patch(`/web-app/club/${clubId}/product/${productId}`, product),
  getProductById: (clubId: string, productId: string) =>
    api.get<Product>(`/web-app/club/${clubId}/product/${productId}`),
  getAll: (clubId: string) =>
    api.get<ApiResponseList<Product>>(`/web-app/club/${clubId}/product`),
  createProduct: (clubId: string, product: CreateProduct) =>
    api.post(`/web-app/club/${clubId}/product`, product),
  deleteProduct: (clubId: string, productId: string) =>
    api.delete(`/web-app/club/${clubId}/product/${productId}`),
};

export const clubApi = {
  getStats: (clubId: string) =>
    api.get<AdminStats[]>(`/web-app/club/${clubId}/stats`),
  updateLogo: (clubId: string, logo: FormData) =>
    api.post(`/web-app/club/${clubId}/logo`, logo),
  updateTournamentById: (
    clubId: string,
    tournamentId: string,
    data: Partial<Tournament>
  ) => api.patch(`/web-app/club/${clubId}/tournament/${tournamentId}`, data),
  addTournamentCategory: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.post(`/web-app/club/${clubId}/tournament/${tournamentId}/category`, {
      categoryId,
    }),
  removeTournamentCategory: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.delete(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}`
    ),
  createRegistration: (
    clubId: string,
    tournamentId: string,
    registration: Partial<CreateRegistration>
  ) =>
    api.post(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration`,
      registration
    ),
  removeRegistration: (
    clubId: string,
    tournamentId: string,
    registrationId: string
  ) =>
    api.delete(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}`
    ),
  getById: (id: string) => api.get(`/web-app/club/${id}`),
  getAll: () => api.get("/web-app/club"),
  update: (id: string, data: Partial<Club>) =>
    api.patch(`/web-app/club/${id}`, data),
  createTournament: (clubId: string, tournament: Partial<Tournament>) =>
    api.post(`/web-app/club/${clubId}/tournament`, tournament),
  getTournaments: (clubId: string) =>
    api.get<{ results: Tournament[]; count: number }>(
      `/web-app/club/${clubId}/tournament`
    ),
  getTournamentById: (clubId: string, tournamentId: string) =>
    api.get(`/web-app/club/${clubId}/tournament/${tournamentId}`),
  getTournamentAvailability: (clubId: string, tournamentId: string) =>
    api.get<{
      courts: Array<{
        court: Court;
        reservations: Array<{
          id: string;
          start: string;
          end: string;
          type: string;
          phase: string | null;
          tournamentId: string | null;
        }>;
      }>;
      tournamentDateRange: {
        startDate: string;
        endDate: string;
        lastDayExcluded: string;
      };
    }>(`/web-app/club/${clubId}/tournament/${tournamentId}/availability`),
  addGroupPhaseSlot: (
    clubId: string,
    tournamentId: string,
    slot: {
      courtId: string;
      start: string | Date;
      end: string | Date;
    }
  ) =>
    api.post<{
      message: string;
      reservation: {
        id: string;
        courtId: string;
        start: string;
        end: string;
      };
    }>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group-phase-slot`,
      slot
    ),
  removeGroupPhaseSlot: (
    clubId: string,
    tournamentId: string,
    reservationId: string
  ) =>
    api.delete<{
      message: string;
    }>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group-phase-slot/${reservationId}`
    ),
  updateEliminationPhase: (
    clubId: string,
    tournamentId: string,
    eliminationPhase: {
      startDate: string | Date;
      startTime: string;
    }
  ) =>
    api.patch<{
      message: string;
      eliminationPhase: {
        startDate: string;
        startTime: string;
      };
    }>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/elimination-phase`,
      eliminationPhase
    ),
  getCourtById: (clubId: string, courtId: string) =>
    api.get(`/web-app/club/${clubId}/court/${courtId}`),

  // Public API
  getPublicTournamentById: (tournamentId: string) =>
    api.get(`/web-app/public/tournament/${tournamentId}`),
  createPublicRegistration: (
    tournamentId: string,
    registration: Partial<CreatePublicRegistration>
  ) =>
    api.post(
      `/web-app/public/tournament/${tournamentId}/registration`,
      registration
    ),
  confirmRegistration: (token: string) =>
    api.post<RegistrationConfirmResponse>(
      `/web-app/public/tournament/registration/confirm/${token}`
    ),
  registerMatchResult: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    matchId: string,
    result: any
  ) =>
    api.patch(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/match/${matchId}/result`,
      result
    ),
  createOpenMatch: (clubId: string, data: CreateOpenMatch) =>
    api.post<WebOpenMatch>(`/web-app/club/${clubId}/open-match`, data),
  getOpenMatches: (clubId: string) =>
    api.get<ApiResponseList<WebOpenMatch>>(`/web-app/club/${clubId}/open-match`),
  updateOpenMatch: (
    clubId: string,
    reservationId: string,
    data: UpdateOpenMatch
  ) =>
    api.patch<WebOpenMatch>(
      `/web-app/club/${clubId}/open-match/${reservationId}`,
      data
    ),
  deleteOpenMatch: (clubId: string, reservationId: string) =>
    api.delete(`/web-app/club/${clubId}/open-match/${reservationId}`),
};

export const courtApi = {
  getReservations: (clubId: string, courtId: string, date?: string) =>
    api.get<{ results: Reservation[] }>(
      `/web-app/club/${clubId}/court/${courtId}/reservation${
        date ? `?date=${date}` : ""
      }`
    ),
  getAll: (clubId: string) =>
    api.get<{ results: Court[]; count: number }>(
      `/web-app/club/${clubId}/court`
    ),
  create: (clubId: string, court: CreateCourt) =>
    api.post(`/web-app/club/${clubId}/court`, court),
  delete: (clubId: string, courtId: string) =>
    api.delete(`/web-app/club/${clubId}/court/${courtId}`),
};

export const categoryApi = {
  getAll: () => api.get<ApiResponseList<Category>>("/web-app/category"),
};

export const pointOfSaleApi = {
  getAll: (clubId: string) =>
    api.get<ApiResponseList<PointOfSale>>(`/web-app/club/${clubId}/pos`),
  create: (clubId: string, pointOfSale: Omit<PointOfSale, "id" | "token">) =>
    api.post(`/web-app/club/${clubId}/pos`, pointOfSale),
  getStartupData: () => api.get<{ club: Club; pos: PointOfSale }>("/pos"),
  updatePointOfSale: (
    clubId: string,
    posId: string,
    pointOfSale: Partial<PointOfSale>
  ) => api.patch(`/web-app/club/${clubId}/pos/${posId}`, pointOfSale),
};

export const playerApi = {
  getPlayers: (clubId: string) =>
    api.get<ApiResponseList<Player>>(`/web-app/club/${clubId}/player`),
  createPlayer: (clubId: string, player: CreatePlayer) =>
    api.post(`/web-app/club/${clubId}/player`, player),
  searchByPhone: (clubId: string, phone: string) =>
    api.get<ApiResponseList<Player>>(
      `/web-app/club/${clubId}/player/search?phone=${phone}`
    ),
  registerPlayer: (clubId: string, playerId: string) =>
    api.post(`/web-app/club/${clubId}/player/${playerId}`),
  searchPlayer: (query: string) =>
    api.get<ApiResponseList<Player>>(`/pos/player/search?query=${query}`),
  deletePlayer: (clubId: string, playerId: string) =>
    api.delete(`/web-app/club/${clubId}/player/${playerId}`),
  createPlayerPos: (player: CreatePlayer) => api.post(`/pos/player`, player),
};

export const contactApi = {
  contact: (reason: string) =>
    api.post<{ success: boolean; message: string }>(
      `/web-app/contact?reason=${reason}`
    ),
};
